import { Accordion, ActionIcon, Badge, Button, Checkbox, Group, Loader, Paper, ScrollArea, Select, Stack, Switch, Text, Tooltip, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { CustomerSafeTicket, UpcomingEvent } from "@msc/shared";

import { useCustomerBranding } from "../../hooks/useCustomerView.js";
import { useEvents, useGenerateSummaries } from "../../hooks/usePresentation.js";
import { groupHeaders } from "../../lib/customerView/deckGroups.js";
import { DECK_MAX } from "../../lib/customerView/deckLimit.js";
import { planGeneration } from "../../lib/customerView/presentPrep.js";
import { usePresentationStore } from "../../stores/presentation.js";
import { AgendaPrep } from "./AgendaPrep.js";
import { StatusBadge } from "./StatusBadge.js";

/**
 * Schritt 1 des Präsentationsmodus: Welche Tickets kommen ins Deck, und in
 * welcher Reihenfolge? Standard ist „alle an“ — im Termin wird eher etwas
 * abgewählt als etwas gesucht.
 *
 * Port aus der Hub-App (`components/PresentPrep.tsx`) auf Mantine. Abweichung:
 * Reihenfolge über Auf/Ab-Knöpfe statt Drag-and-drop — mit der Tastatur
 * bedienbar und ohne eine zweite Zustandsquelle neben dem Store.
 */
interface Props {
  tickets: CustomerSafeTicket[];
  customerObjectKey: string;
  /** Name des Kunden — die Agenda-Abfrage braucht ihn für die Jira-Suche. */
  customerName: string;
  bereichName: string | null;
}

export function PresentPrep({ tickets, customerObjectKey, customerName, bereichName }: Props) {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  // Gleiches Muster und derselbe Versatz wie im Cockpit (`PresenterCockpit`,
  // Begründung dort): Ab `md` bekommt die Vorbereitung eine feste Höhe, damit
  // die Ticketliste darunter eine BEGRENZTE Bezugshöhe hat und sich der Platz
  // zwischen Agenda-Ausklapper und Liste tatsächlich aufteilen lässt. Darunter
  // bleibt alles gestapelt und fließend hoch.
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.md})`) ?? true;

  const phase = usePresentationStore((s) => s.phase);
  const deckKeys = usePresentationStore((s) => s.deckKeys);
  const deckGroups = usePresentationStore((s) => s.deckGroups);
  const headers = useMemo(() => groupHeaders(deckKeys, (k) => deckGroups[k]), [deckKeys, deckGroups]);
  const selected = usePresentationStore((s) => s.selected);
  const toggleSelect = usePresentationStore((s) => s.toggleSelect);
  const selectAll = usePresentationStore((s) => s.selectAll);
  const selectByStatus = usePresentationStore((s) => s.selectByStatus);
  const move = usePresentationStore((s) => s.move);
  const cancelPrep = usePresentationStore((s) => s.cancelPrep);
  const beginGenerating = usePresentationStore((s) => s.beginGenerating);
  const beginReview = usePresentationStore((s) => s.beginReview);
  const backToPrep = usePresentationStore((s) => s.backToPrep);
  const withSummaries = usePresentationStore((s) => s.withSummaries);
  const setWithSummaries = usePresentationStore((s) => s.setWithSummaries);

  const generate = useGenerateSummaries();
  // Termine der Bereichs-Eventseite für die Endfolie: schon hier laden, damit
  // sie beim Start meist nur noch aus dem Cache kommen.
  const events = useEvents(bereichName, true);
  // Ebenso das Kundendesign (D10): Der Start im Prüfschritt entscheidet daran,
  // ob die Bühne im Design des Kunden beginnt — dann steht es schon im Cache.
  useCustomerBranding(customerObjectKey);

  const byKey = useMemo(() => new Map(tickets.map((x) => [x.key, x])), [tickets]);
  const statuses = useMemo(() => Array.from(new Set(tickets.map((x) => x.status))).sort(), [tickets]);

  const selectedCount = deckKeys.filter((k) => selected[k]).length;
  const generating = phase === "generating";
  // `issueKeysSchema` in `@msc/shared` lässt höchstens `DECK_MAX` Schlüssel zu.
  // Hier zu bremsen ist der einzige Ort, an dem der Lead noch etwas tun kann —
  // sonst scheitert erst der Aufruf, und die Meldung nennt den Grund nicht.
  const tooMany = selectedCount > DECK_MAX;

  /**
   * Termine sind fail-soft: ohne sie bleibt nur das Raster der Endfolie leer.
   * Der Vorlauf (`useEvents(..., true)`) hat sie meist schon — dann kostet der
   * Start keinen zweiten Aufruf.
   */
  const loadUpcoming = async (): Promise<UpcomingEvent[] | null> => {
    try {
      const data = events.data ?? (await events.refetch()).data;
      return data?.events ?? null;
    } catch {
      return null;
    }
  };

  const start = async () => {
    if (selectedCount === 0 || tooMany || generating) return;
    // Ohne Zusammenfassungen bleibt der LLM-Aufruf aus — nicht nur sein
    // Ergebnis ungenutzt (s. `planGeneration`). Die Termine der Endfolie
    // kommen in beiden Fällen.
    const plan = planGeneration({ withSummaries });
    beginGenerating();
    const deck = usePresentationStore.getState().deck;
    try {
      const [res, upcoming] = await Promise.all([
        plan.summaries ? generate.mutateAsync({ customerObjectKey, issueKeys: deck }) : null,
        plan.events ? loadUpcoming() : null,
      ]);

      if (res !== null && res.missingKeys.length > 0) {
        notifications.show({
          color: "yellow",
          message: t("pages.customerView.present.missingKeys", { keys: res.missingKeys.join(", ") }),
        });
        // Ohne Ticket keine Folie: abwählen und das Deck neu bilden
        // (`beginGenerating` filtert `deckKeys` über `selected`).
        for (const k of res.missingKeys) {
          if (usePresentationStore.getState().selected[k]) toggleSelect(k);
        }
        beginGenerating();
        // Waren ALLE Tickets verschwunden, bliebe ein leeres Deck übrig — die
        // Präsentation liefe dann in eine Agenda ohne Zeilen und eine Bühne
        // ohne Folie. Zurück zur Auswahl ist der einzige sinnvolle Ausgang.
        if (usePresentationStore.getState().deck.length === 0) {
          notifications.show({ color: "yellow", message: t("pages.customerView.present.emptyDeck") });
          backToPrep();
          return;
        }
      }

      // Ohne Zusammenfassungen geht eine LEERE Karte in den Review-Schritt:
      // Dort steht je Ticket ein leeres Feld, und die Folie fällt auf den
      // letzten öffentlichen Kommentar zurück (`buildStageSnapshot`).
      beginReview(res?.summaries ?? [], upcoming);
    } catch {
      notifications.show({ color: "red", message: t("pages.customerView.present.generateFailed") });
      backToPrep();
    }
  };

  return (
    <Stack
      gap="sm"
      style={isDesktop ? { height: "calc(100vh - 320px)", minHeight: 0 } : { flex: 1, minHeight: 0 }}
    >
      {/*
        Die Agenda-Punkte stehen VOR der Ticketauswahl, weil sie im Termin
        vorne laufen; offen, weil sie der erste Blick des Leads sind. Die Zahl
        am Weiter-Knopf zählt weiterhin nur Tickets: Sie beschreibt, wie viele
        Zusammenfassungen erzeugt werden, und Agenda-Punkte kosten keinen
        LLM-Aufruf.

        Der Ausklapper darf SCHRUMPFEN (`flexShrink: 1` mit `minHeight: 0`) und
        scrollt dann selbst: Drei Punkte mit ihren Textfeldern sind höher als
        der Bildschirm, und ohne das schöbe er die Ticketliste ganz hinaus.
        Zugeklappt gibt er seinen Platz an die Liste zurück — genau darum geht
        es hier.

        `overflowY: auto` sitzt bewusst auf dem PANEL (`styles.panel`), nicht
        auf der Accordion-Wurzel: Stand es dort, scrollte die Kopfzeile
        „Agenda-Punkte" mit aus dem Bild, sobald ein Textfeld im Panel den
        Browser zum Scrollen ins Sichtfeld zwang (Review-Fund) — der Lead
        verlor den Ausklapper-Kopf und damit die Möglichkeit, ihn wieder
        zuzuklappen. `item` und `panel` bilden dafür ihrerseits eine
        Flex-Spalte (`minHeight: 0`), damit die von der Wurzel zugewiesene,
        ggf. geschrumpfte Höhe an das Panel durchgereicht wird, statt am Item
        vorbei am Inhalt gemessen zu werden.
      */}
      <Accordion
        variant="contained"
        defaultValue="agenda"
        style={{ flexShrink: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
        styles={{
          item: { display: "flex", flexDirection: "column", minHeight: 0, flex: "1 1 auto" },
          panel: { display: "flex", flexDirection: "column", minHeight: 0, flex: "1 1 auto", overflowY: "auto" },
        }}
      >
        <Accordion.Item value="agenda">
          <Accordion.Control>{t("pages.customerView.present.agendaSection")}</Accordion.Control>
          <Accordion.Panel>
            <AgendaPrep customerObjectKey={customerObjectKey} customerName={customerName} />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      <Group justify="space-between" wrap="wrap" gap="sm" style={{ flexShrink: 0 }}>
        <Group gap="sm" wrap="wrap">
          <Text fw={600}>{t("pages.customerView.present.prepTitle")}</Text>
          <Text size="sm" c="dimmed">
            {t("pages.customerView.present.selectedCount", { n: selectedCount, total: deckKeys.length })}
          </Text>
          {tooMany && (
            <Text size="sm" c="red">{t("pages.customerView.present.deckFull")}</Text>
          )}
          <Button size="compact-xs" variant="default" disabled={generating} onClick={() => selectAll(true)}>
            {t("pages.customerView.present.selectAll")}
          </Button>
          <Button size="compact-xs" variant="default" disabled={generating} onClick={() => selectAll(false)}>
            {t("pages.customerView.present.selectNone")}
          </Button>
          <Select
            size="xs"
            w={220}
            value={null}
            disabled={generating}
            data={statuses}
            placeholder={t("pages.customerView.present.selectByStatus")}
            aria-label={t("pages.customerView.present.selectByStatus")}
            onChange={(v) => {
              if (v) selectByStatus(v, tickets);
            }}
          />
        </Group>
        <Group gap="sm">
          <Switch
            size="xs"
            checked={withSummaries}
            disabled={generating}
            onChange={(e) => setWithSummaries(e.currentTarget.checked)}
            label={t("pages.customerView.present.withSummaries")}
          />
          <Button variant="subtle" disabled={generating} onClick={cancelPrep}>
            {t("pages.customerView.present.cancel")}
          </Button>
          <Button disabled={selectedCount === 0 || tooMany || generating} loading={generating} onClick={() => void start()}>
            {generating
              ? t("pages.customerView.present.generating")
              : withSummaries
                ? t("pages.customerView.present.generate", { n: selectedCount })
                : t("pages.customerView.present.startWithoutSummaries", { n: selectedCount })}
          </Button>
        </Group>
      </Group>

      {generating && (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="sm" c="dimmed">{t("pages.customerView.present.generatingHint")}</Text>
        </Group>
      )}

      {/* `flex-basis` statt 0: Die Liste behält eine Grundhöhe, auch wenn der
          Ausklapper darüber offen ist — sonst bliebe bei voller Agenda nichts
          von ihr übrig. Die ScrollArea füllt das begrenzte Paper („100%"),
          statt eine eigene, vom Zustand des Ausklappers unabhängige Höhe zu
          behaupten. */}
      <Paper withBorder style={{ flex: "1 1 240px", minHeight: 240 }}>
        <ScrollArea h={isDesktop ? "100%" : "calc(100vh - 300px)"}>
          <Stack gap={0}>
            {deckKeys.map((key, i) => {
              const ticket = byKey.get(key);
              if (!ticket) return null;
              const on = selected[key] === true;
              const header = headers.get(i);
              return (
                <Fragment key={key}>
                {header !== undefined && (
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase" px="sm" pt={i === 0 ? 8 : 14} pb={4}>
                    {header}
                  </Text>
                )}
                <Group
                  gap="sm"
                  wrap="nowrap"
                  px="sm"
                  py={8}
                  style={{
                    opacity: on ? 1 : 0.45,
                    borderTop: i === 0 ? undefined : "1px solid var(--mantine-color-gray-2)",
                  }}
                >
                  <Checkbox
                    checked={on}
                    disabled={generating}
                    aria-label={`${ticket.key} ${ticket.title}`}
                    onChange={() => toggleSelect(key)}
                  />
                  <Text size="xs" c="dimmed" ff="monospace" w={28} ta="right">{i + 1}.</Text>
                  <Text size="xs" c="dimmed" ff="monospace">{ticket.key}</Text>
                  <Text size="sm" lineClamp={1} style={{ flex: 1, minWidth: 0 }}>{ticket.title}</Text>
                  {ticket.steering && (
                    <Tooltip label={t("pages.customerView.steeringHint")} withArrow>
                      <Badge size="xs" color="gray" variant="light">{t("pages.customerView.steering")}</Badge>
                    </Tooltip>
                  )}
                  <StatusBadge status={ticket.status} category={ticket.statusCategory} />
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    disabled={generating || i === 0}
                    aria-label={t("pages.customerView.present.moveUp")}
                    onClick={() => move(key, -1)}
                  >
                    ↑
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    disabled={generating || i === deckKeys.length - 1}
                    aria-label={t("pages.customerView.present.moveDown")}
                    onClick={() => move(key, 1)}
                  >
                    ↓
                  </ActionIcon>
                </Group>
                </Fragment>
              );
            })}
          </Stack>
        </ScrollArea>
      </Paper>
    </Stack>
  );
}
